import React from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent, useCollectionBinding, useContentToken } from '@vibesites/runtime/react';
import { useRevealStyles } from '@vibesites/theme-prestige/hooks/useRevealStyles';
import {
  AmenityColumn,
  AmenitiesStyles,
  stripHtml,
  type Amenity,
  type GalleryPhoto,
} from '@vibesites/theme-prestige/components/AmenitiesShared';

// Keywords that mark a photo_gallery item as an *exterior building* shot
// (building facade, aerial/drone, street view, etc.) rather than a photo of a
// community amenity. RR Living does not want these external building images in
// the community amenities gallery, so any photo whose title / alt text /
// description mentions one of these terms is filtered out below.
//
// Deliberately narrow: only high-precision terms that unambiguously describe a
// building-facade or aerial shot. Broad words like "building", "exterior",
// "outside", and "external" were removed because they also match legitimate
// community amenity photos (e.g. "clubhouse building", "outside pool deck"),
// which over-filtered the gallery and hid real community assets.
const EXTERIOR_BUILDING_KEYWORDS = [
  'facade',
  'façade',
  'aerial',
  'drone',
  'street view',
  'streetview',
];

function isExteriorBuildingPhoto(photo: GalleryPhoto): boolean {
  const haystack = [photo.title, photo.alt_text, photo.description]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!haystack) return false;
  return EXTERIOR_BUILDING_KEYWORDS.some((kw) => haystack.includes(kw));
}

export function CommunityAmenitiesList() {
  const headingRaw = useContentToken('communityamenitieslist.heading');
  const headingText = stripHtml(headingRaw) || 'Community Amenities';

  const items = useCollectionBinding<Amenity>('communityamenitieslist.items', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });

  const allPhotos = useCollectionBinding<GalleryPhoto>('communityamenitieslist.photos', {
    collection: 'photo_gallery',
    filter: { title: 'Community' },
    defaultLimit: 8,
  });

  // Drop exterior building images — keep only amenity-focused photos.
  const photos = allPhotos.filter((photo) => !isExteriorBuildingPhoto(photo));

  useRevealStyles();

  return (
    <section className="py-8 md:py-12 bg-[var(--vibe-color-background)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {items.length > 0 ? (
          <AmenityColumn
            headingNode={
              <RichTextContent
                path="communityamenitieslist.heading"
                label="Community Heading"
                defaultValue="<p>Community Amenities</p>"
                as="h3"
                className="amenities-col-heading"
              />
            }
            headingText={headingText}
            items={items}
            photos={photos}
            imagePosition="left"
          />
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
            <p className="font-body text-lg">Community amenities will be displayed here.</p>
          </div>
        )}
      </div>

      <AmenitiesStyles />
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'CommunityAmenitiesList',
  displayName: 'Community Amenities List',
  tokens: {
    'communityamenitieslist.heading': {
      label: 'Community Heading',
      type: 'richtext',
      defaultValue: '<p>Community Amenities</p>',
    },
  },
  collectionBindings: {
    'communityamenitieslist.items': {
      label: 'Community Amenities',
      collection: 'amenities',
    },
    'communityamenitieslist.photos': {
      label: 'Community Photos',
      collection: 'photo_gallery',
    },
  },
};

(CommunityAmenitiesList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default CommunityAmenitiesList;
